import { type ApiFetchContext, apiFetch } from "../client";
import type { BuildHeadersInput } from "../headers";
import type {
  BillPage,
  BillPageRaw,
  BillRecord,
  BillRecordRaw,
  WithdrawApply,
  WithdrawApplyInput,
  WithdrawApplyRaw,
  WithdrawPage,
  WithdrawPageRaw,
} from "../types/wallet";
import type { LangOpts } from "./product";

function normalizeWithdraw(raw: WithdrawApplyRaw): WithdrawApply {
  return {
    id: raw.id,
    amountRubKopecks: Math.round((raw.amount ?? 0) * 100),
    type: raw.type ?? "amount",
    status: raw.status ?? "audit",
    statusName: raw.status ?? "",
    message: raw.message ?? "",
    payeeName: raw.payeeName ?? "",
    payeeAccount: raw.payeeAccount ?? "",
    payeeBank: raw.payeeBank ?? "",
    currency: raw.currency ?? "RUB",
    createTime: raw.createTime ?? "",
  };
}

function normalizeBill(raw: BillRecordRaw): BillRecord {
  // #575 — Authoritative direction signal: balance delta.
  //
  // Why not `incomeAmount`: backend writes `incomeAmount = changeAmount` for
  // CONSUME / refund / manual-add records alike (verified 2026-05-28 on
  // staging DB — every row has incomeAmount === changeAmount as positive
  // magnitudes). So `incomeAmount > 0` is true for spending too and flips
  // the sign wrong. PRs #564/#567/#569/#570/#574 all missed this because the
  // documented contract ("incomeAmount = 0 for spending") never matched.
  //
  // Why not the `type` text: `vo.setType(typeDictItemsMap.get(...))` ships
  // localized labels ("消费" / "Расход" / …) — pattern-matching on the client
  // would be brittle across languages.
  //
  // `balanceAfter - balanceBefore` is a pure accounting identity: every credit
  // grows the balance, every debit shrinks it. Robust across any current or
  // future `type` code, any locale, no server change required.
  const magnitude = Math.round((raw.changeAmount ?? 0) * 100);
  const before = typeof raw.balanceBeforeChange === "number" ? raw.balanceBeforeChange : null;
  const after = typeof raw.balanceAfterChange === "number" ? raw.balanceAfterChange : null;
  let sign: 0 | 1 | -1 = 0;
  if (before !== null && after !== null) {
    if (after > before) sign = 1;
    else if (after < before) sign = -1;
  }
  // Fallback when balance snapshots are missing (legacy rows) or equal (zero
  // delta): keep the absolute magnitude. Showing it un-signed is less wrong
  // than flipping it negative.
  const changeRubKopecks = sign === -1 ? -magnitude : magnitude;
  const income = Math.round((raw.incomeAmount ?? 0) * 100);
  return {
    id: raw.id,
    type: raw.type ?? "",
    changeRubKopecks,
    currency: raw.changeAmountCurrency ?? "RUB",
    currencySymbol: raw.changeAmountCurrencySymbol ?? "₽",
    changeTime: raw.changeTime ?? "",
    incomeRubKopecks: income,
    orderNo: raw.orderNo ?? "",
    payOrderNo: raw.payOrderNo ?? "",
    remark: raw.changeRmk ?? "",
  };
}

/** POST /balance/withdraw/apply — submit a withdraw request. */
export async function applyWithdraw(
  ctx: ApiFetchContext,
  authToken: string,
  input: WithdrawApplyInput,
): Promise<string> {
  return apiFetch<string>(ctx, "/balance/withdraw/apply", {
    method: "POST",
    body: input,
    authToken,
  });
}

/** GET /balance/withdraw/list — paged withdraw history. */
export async function listWithdraws(
  ctx: ApiFetchContext,
  authToken: string,
  input: { pageNo: number; pageSize: number; type?: string } = {
    pageNo: 1,
    pageSize: 10,
  },
): Promise<WithdrawPage> {
  const parts = [
    `pageNo=${input.pageNo}`,
    `pageSize=${input.pageSize}`,
    ...(input.type ? [`type=${encodeURIComponent(input.type)}`] : []),
  ];
  const raw = await apiFetch<WithdrawPageRaw>(ctx, `/balance/withdraw/list?${parts.join("&")}`, {
    authToken,
  });
  return {
    records: (raw.records ?? []).map(normalizeWithdraw),
    total: raw.total ?? 0,
    pageNo: raw.current ?? input.pageNo,
    pageSize: raw.size ?? input.pageSize,
    pages: raw.pages ?? 1,
  };
}

/** GET /balance/withdraw/detail?withdrawApplyId */
export async function getWithdrawDetail(
  ctx: ApiFetchContext,
  authToken: string,
  withdrawApplyId: string,
): Promise<WithdrawApply> {
  const raw = await apiFetch<WithdrawApplyRaw>(
    ctx,
    `/balance/withdraw/detail?withdrawApplyId=${encodeURIComponent(withdrawApplyId)}`,
    { authToken },
  );
  return normalizeWithdraw(raw);
}

/**
 * POST /balance/recharge — create a wallet top-up order.
 *
 * #467: the frontend submits the user's typed amount *in their display
 * currency* verbatim (`rechargeAmount`) plus that `currency`. The backend
 * resolves the credited CNY value using its authoritative marked-up exchange
 * rate (`ExchangeRateUtils`) — the client never decides/tampers the credited
 * amount (the hole behind #462's frontend RUB→CNY conversion). `currency` is
 * optional on the wire: a backend that hasn't yet shipped #467 support ignores
 * it and treats `rechargeAmount` as CNY (legacy default), so this client must
 * only ship *after* the backend `currency` support is live.
 *
 * The backend creates a `PayOrder` of type RECHARGE and returns its `orderNo`;
 * that is a normal pay order, so it drives the same cashier as product orders
 * (`/channel/list` → `/order/pay`). The returned string is the recharge
 * `orderNo` to hand to the /pay page.
 */
export async function rechargeBalance(
  ctx: ApiFetchContext,
  authToken: string,
  body: { rechargeAmount: number; currency?: string },
): Promise<string> {
  return apiFetch<string>(ctx, "/balance/recharge", {
    method: "POST",
    body: { rechargeAmount: body.rechargeAmount, currency: body.currency },
    authToken,
  });
}

/** GET /payment/bill — paged wallet/coin change history. */
export async function listBill(
  ctx: ApiFetchContext,
  authToken: string,
  input: {
    pageNo: number;
    pageSize: number;
    changeType?: string;
    orderNo?: string;
    payOrderNo?: string;
  },
  opts: LangOpts & Pick<BuildHeadersInput, "currency"> = {},
): Promise<BillPage> {
  const parts = [
    `pageNo=${input.pageNo}`,
    `pageSize=${input.pageSize}`,
    ...(input.changeType ? [`changeType=${encodeURIComponent(input.changeType)}`] : []),
    ...(input.orderNo ? [`orderNo=${encodeURIComponent(input.orderNo)}`] : []),
    ...(input.payOrderNo ? [`payOrderNo=${encodeURIComponent(input.payOrderNo)}`] : []),
  ];
  // Thread the backend `lang` header so the bill `type` (dict-localized) and
  // `changeRmk` (RU-translated server-side) match the active UI locale. Without
  // it, buildHeaders falls back to brand default (ru_RU) regardless of the UI
  // locale — the bug behind untranslated wallet history.
  const raw = await apiFetch<BillPageRaw>(ctx, `/payment/bill?${parts.join("&")}`, {
    authToken,
    ...(opts.lang === undefined ? {} : { lang: opts.lang }),
    ...(opts.currency === undefined ? {} : { currency: opts.currency }),
  });
  return {
    records: (raw.records ?? []).map(normalizeBill),
    total: raw.total ?? 0,
    pageNo: raw.current ?? input.pageNo,
    pageSize: raw.size ?? input.pageSize,
    pages: raw.pages ?? 1,
  };
}
